import { INITIED_STATUS } from 'constants/trainingWishStatus'
import { iriToId } from 'services/apiPlatform'
import type { TrainingWish } from 'shared/types'
import { round2Decimals } from 'utils/number'

interface MapPayloadProps {
  formData: FormData
  edit?: boolean
  trainingId?: number
  simpleForm?: boolean
}
interface MapFreeWishPayloadProps {
  formData: FormData
}
interface Plan {
  cost: string
  percent: string
  plan: string
}
interface MapAccreditationPayloadProps {
  formData: FormData
  trainingId?: number
  accreditationId?: number
}
interface Payload {
  filters?: {
    users?: number[] | undefined
    managers?: number[] | undefined
    withInactives: boolean
    withExternals: boolean
  }
  cost?: string
  durationHours?: string
  trainingId?: number
  plans?: Plan[]
  'plans-length'?: number
  category?: string
  theme?: string
  users?: number
  managers?: number
  trainers?: string
}

export const mapPayload = ({ formData, trainingId }: MapPayloadProps) => {
  const data = Object.fromEntries(formData)

  const payload: Payload = {
    ...data,
    ...(trainingId ? { trainingId } : {}),
  }

  // Workaround for plans
  const plans: Plan[] = []
  for (let i = 0; i < Number(data['plans-length']); i += 1) {
    if (data[`plan-${i}`] !== '') {
      plans[i] = {
        cost: data[`cost-${i}`].toString(),
        percent: data[`percent-${i}`].toString(),
        plan: data[`plan-${i}`].toString(),
      }
    }
    // @ts-expect-error
    delete payload[`cost-${i}`]
    // @ts-expect-error
    delete payload[`percent-${i}`]
    // @ts-expect-error
    delete payload[`plan-${i}`]
  }

  // Cleanup
  delete payload.managers
  delete payload.users
  delete payload['plans-length']

  return {
    ...payload,
    ...(plans.length ? { plans } : {}),
  }
}

export const mapFreeWishPayload = ({ formData }: MapFreeWishPayloadProps) => {
  const payload = mapPayload({ formData })

  return {
    ...payload,
    ...(payload.cost && { cost: Math.round(Number(payload.cost) * 100) / 100 }),
    ...(payload.durationHours && { durationHours: Math.round(Number(payload.durationHours) * 100) / 100 }),
    // eslint-disable-next-line max-len
    ...(payload.category
      ? { category: !Number.isNaN(payload.category) ? iriToId(payload.category) : payload.category }
      : {}),
    ...(payload.theme ? { theme: !Number.isNaN(payload.theme) ? iriToId(payload.theme) : payload.theme } : {}),
    trainers: (JSON.parse(payload.trainers ?? '[]') ? JSON.parse(payload.trainers ?? '[]') : []).map((t: any) => ({
      trainer: t.trainer?.id,
      organization: t.organization?.id,
    })),
  }
}

export const mapAccreditationPayload = ({ formData, trainingId, accreditationId }: MapAccreditationPayloadProps) => {
  const payload = mapPayload({ formData })

  return {
    trainingId,
    accreditationId,
    ...payload,
  }
}

export const getCost = (wish: TrainingWish) => {
  if (wish?.currentProvisions) return round2Decimals(wish.currentProvisions * wish.training.cost)
  return round2Decimals(wish.training.cost)
}

// NeedsSummary GroupActions
export const hasArbitrateAction = (rows: TrainingWish[]) =>
  rows.filter((row: TrainingWish) => !!row?.actions?.arbitrate)
export const hasConfirmAction = (rows: TrainingWish[]) => rows.filter((row: TrainingWish) => !!row?.actions?.confirm)
export const hasValidateAction = (rows: TrainingWish[]) => rows.filter((row: TrainingWish) => !!row?.actions?.validate)
export const hasValidateActionAndStatus = (rows: TrainingWish[]) =>
  rows.filter((row: TrainingWish) => !!row?.actions?.validate && row.status !== INITIED_STATUS)
